// src/components/modals/EditRecipeModal.jsx
import { useState, useEffect } from 'react'
import { X, Plus, Trash2, ChevronDown } from 'lucide-react'
import PropTypes from 'prop-types'

const EditRecipeModal = ({ isOpen, onClose, onSubmit, recipe, products, rawMaterials }) => {
  const [formData, setFormData] = useState({
    name: '',
    cost: '',
    product: '',
    rawMaterialList: [],
    isActive: true
  })

  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [selectedRawMaterial, setSelectedRawMaterial] = useState('')

  // Initialize form with recipe data
  useEffect(() => {
    if (isOpen && recipe) {
      setFormData({
        name: recipe.name || '',
        cost: recipe.cost?.toString() || '',
        product: recipe.product?._id || recipe.product || '',
        rawMaterialList: recipe.rawMaterialList?.map(item => ({
          rawMaterial: item.rawMaterial?._id || item.rawMaterial,
          quantity: item.quantity || 0,
          materialData: item.rawMaterial || rawMaterials.find(rm => rm._id === (item.rawMaterial?._id || item.rawMaterial))
        })) || [],
        isActive: recipe.isActive !== false
      })
      setErrors({})
      setSelectedRawMaterial('')
    }
  }, [isOpen, recipe, rawMaterials])

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  // Add raw material to list
  const addRawMaterial = () => {
    if (!selectedRawMaterial) {
      setErrors(prev => ({
        ...prev,
        rawMaterial: 'Please select a raw material'
      }))
      return
    }

    // Check if material already exists
    const exists = formData.rawMaterialList.some(item => item.rawMaterial === selectedRawMaterial)
    if (exists) {
      setErrors(prev => ({
        ...prev,
        rawMaterial: 'This raw material is already added'
      }))
      return
    }

    const material = rawMaterials.find(rm => rm._id === selectedRawMaterial)
    if (!material) {
      setErrors(prev => ({
        ...prev,
        rawMaterial: 'Invalid raw material selected'
      }))
      return
    }

    const newItem = {
      rawMaterial: selectedRawMaterial,
      quantity: '', // Start with empty string instead of 0
      materialData: material // Store for display purposes
    }

    setFormData(prev => ({
      ...prev,
      rawMaterialList: [...prev.rawMaterialList, newItem]
    }))

    setSelectedRawMaterial('')
    setErrors(prev => ({
      ...prev,
      rawMaterial: ''
    }))
  }

  // Remove raw material from list
  const removeRawMaterial = (index) => {
    setFormData(prev => ({
      ...prev,
      rawMaterialList: prev.rawMaterialList.filter((_, i) => i !== index)
    }))
  }

  // Update raw material quantity
  const updateRawMaterialQuantity = (index, quantity) => {
    setFormData(prev => ({
      ...prev,
      rawMaterialList: prev.rawMaterialList.map((item, i) =>
        i === index ? { ...item, quantity: quantity } : item
      )
    }))
  }

  // Validate form
  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Recipe name is required'
    }

    if (!formData.product) {
      newErrors.product = 'Product selection is required'
    }

    if (!formData.cost || isNaN(formData.cost) || parseFloat(formData.cost) < 0) {
      newErrors.cost = 'Valid cost is required'
    }

    if (formData.rawMaterialList.length === 0) {
      newErrors.rawMaterialList = 'At least one raw material is required'
    }

    // Validate raw material quantities
    const invalidQuantities = formData.rawMaterialList.some(item =>
      !item.quantity || item.quantity === '' || isNaN(item.quantity) || parseFloat(item.quantity) <= 0
    )

    if (invalidQuantities) {
      newErrors.rawMaterialList = 'All raw materials must have valid quantities greater than 0'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      // Prepare data for API
      const submitData = {
        name: formData.name.trim(),
        cost: parseFloat(formData.cost),
        product: formData.product,
        rawMaterialList: formData.rawMaterialList.map(item => ({
          rawMaterial: item.rawMaterial,
          quantity: parseFloat(item.quantity) || 0
        })),
        isActive: formData.isActive
      }

      await onSubmit(submitData)
    } catch (error) {
      console.error('Error updating recipe:', error)
      setErrors({ submit: error.message || 'Failed to update recipe' })
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen || !recipe) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Edit Recipe</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          {/* Recipe Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recipe Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="eg.Espresso Standard"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              disabled={loading}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          {/* Cost and Product */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            {/* Cost */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cost *
              </label>
              <input
                type="number"
                name="cost"
                value={formData.cost}
                onChange={handleInputChange}
                placeholder="0.00"
                step="0.01"
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                disabled={loading}
              />
              {errors.cost && (
                <p className="mt-1 text-sm text-red-600">{errors.cost}</p>
              )}
            </div>

            {/* Product */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product name *
              </label>
              <div className="relative">
                <select
                  name="product"
                  value={formData.product}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none"
                  disabled={loading}
                >
                  <option value="">Select Product</option>
                  {products.length === 0 ? (
                    <option disabled>No recipe-based products available</option>
                  ) : (
                    products.map(product => (
                      <option key={product._id} value={product._id}>
                        {product.name}
                      </option>
                    ))
                  )}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
              {errors.product && (
                <p className="mt-1 text-sm text-red-600">{errors.product}</p>
              )}
            </div>
          </div>

          {/* Raw Material List */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Raw Material List
            </label>

            {/* Add Raw Material Section */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex gap-2 mb-2">
                <div className="flex-1 relative">
                  <select
                    value={selectedRawMaterial}
                    onChange={(e) => {
                      setSelectedRawMaterial(e.target.value)
                      if (errors.rawMaterial) {
                        setErrors(prev => ({ ...prev, rawMaterial: '' }))
                      }
                    }}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 appearance-none"
                    disabled={loading}
                  >
                    <option value="">Select Raw Material</option>
                    {rawMaterials
                      .filter(material => !formData.rawMaterialList.some(item => item.rawMaterial === material._id))
                      .map(material => (
                        <option key={material._id} value={material._id}>
                          {material.name} ({material.unit?.abbreviation || 'unit'})
                        </option>
                      ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                </div>
                <button
                  type="button"
                  onClick={addRawMaterial}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
                  disabled={loading}
                >
                  <Plus size={16} />
                  Add
                </button>
              </div>
              {errors.rawMaterial && (
                <p className="text-sm text-red-600">{errors.rawMaterial}</p>
              )}
            </div>

            {/* Raw Material List Display */}
            {formData.rawMaterialList.length > 0 && (
              <div className="space-y-2">
                <div className="grid grid-cols-12 gap-2 text-sm font-medium text-gray-700 px-2">
                  <div className="col-span-4">Raw material</div>
                  <div className="col-span-2">Unit</div>
                  <div className="col-span-4">Qty usage</div>
                  <div className="col-span-2">Actions</div>
                </div>

                {formData.rawMaterialList.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 items-center p-2 bg-gray-50 rounded">
                    <div className="col-span-4 text-sm">
                      {item.materialData?.name || 'Unknown Material'}
                    </div>
                    <div className="col-span-2 text-sm text-gray-600">
                      {item.materialData?.unit?.abbreviation || item.materialData?.unit?.name || item.materialData?.unit || 'N/A'}
                    </div>
                    <div className="col-span-4">
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateRawMaterialQuantity(index, e.target.value)}
                        placeholder="Enter quantity"
                        step="0.001"
                        min="0"
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-orange-500"
                        disabled={loading}
                      />
                    </div>
                    <div className="col-span-2">
                      <button
                        type="button"
                        onClick={() => removeRawMaterial(index)}
                        className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                        disabled={loading}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {errors.rawMaterialList && (
              <p className="mt-2 text-sm text-red-600">{errors.rawMaterialList}</p>
            )}
          </div>

          {/* Status Switch */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                  Status
                </label>
                {/* <p className="text-xs text-gray-500">
                  {formData.isActive ? 'Recipe is active and available for use' : 'Recipe is inactive and hidden'}
                </p> */}
              </div>
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, isActive: !prev.isActive }))}
                  disabled={loading}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 ${formData.isActive ? 'bg-orange-500' : 'bg-gray-200'
                    }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${formData.isActive ? 'translate-x-6' : 'translate-x-1'
                      }`}
                  />
                </button>
                {/* <span className="ml-3 text-sm text-gray-700">
                  {formData.isActive ? 'Active' : 'Inactive'}
                </span> */}
              </div>
            </div>
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Recipe'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

EditRecipeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  recipe: PropTypes.object,
  products: PropTypes.array.isRequired,
  rawMaterials: PropTypes.array.isRequired
}

export default EditRecipeModal